import { useState, useEffect, useCallback, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import {
  EntityInstance,
  EntityWithRelationsResponse,
  EntityCountResponse,
  SearchableFieldsResponse,
  SearchableRelation,
  SearchableField,
  EntitySearchResponse,
} from '../types';

export interface RelatedFieldSearch {
  relationPath: string;
  fieldName: string;
  searchValue: string;
}

export const useEntityVisualization = (tableName: string) => {
  const { session } = useSession();

  const [entity, setEntity] = useState<EntityInstance | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Related field search state
  const [searchableDirectFields, setSearchableDirectFields] = useState<SearchableField[]>([]);
  const [searchableRelations, setSearchableRelations] = useState<SearchableRelation[]>([]);
  const [searchResults, setSearchResults] = useState<EntityInstance[]>([]);
  const [searchResultIndex, setSearchResultIndex] = useState<number>(0);
  const [totalSearchMatches, setTotalSearchMatches] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [activeRelatedSearch, setActiveRelatedSearch] = useState<RelatedFieldSearch | null>(null);

  // Ref to track if initial data has been loaded (prevents re-fetch on tab switch)
  const isInitializedRef = useRef<boolean>(false);
  const currentTableNameRef = useRef<string>(tableName);

  /**
   * Fetch entity with relations
   */
  const fetchEntity = useCallback(
    async (entityId?: string) => {
      if (!session) {
        console.log('⏳ Session not ready, waiting...');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`🔄 Fetching entity from table: ${tableName}${entityId ? ` with ID: ${entityId}` : ' (random)'}`);

        const queryParams = new URLSearchParams();
        if (entityId) {
          queryParams.append('id', entityId);
        }
        queryParams.append('depth', '2');

        const url = `${API_URL}/document-generation/entities/${tableName}/with-relations?${queryParams.toString()}`;

        const response = await fetchWithAuth(url, { method: 'GET' }, session);

        if (!response.ok) {
          throw new Error(`Failed to fetch entity: ${response.statusText}`);
        }

        const data: EntityWithRelationsResponse = await response.json();

        if (data.success && data.entity) {
          console.log('✅ Entity fetched successfully:', data.entity);
          setEntity(data.entity);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err: any) {
        console.error('❌ Error fetching entity:', err);
        setError(err.message || 'Failed to fetch entity');
        toast.error(`Fout bij ophalen entiteit: ${err.message}`, {
          position: 'top-right',
        });
      } finally {
        setLoading(false);
      }
    },
    [tableName, session]
  );

  /**
   * Fetch total entity count
   */
  const fetchEntityCount = useCallback(async () => {
    if (!session) return;

    try {
      const url = `${API_URL}/document-generation/entities/${tableName}/count`;
      const response = await fetchWithAuth(url, { method: 'GET' }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch count: ${response.statusText}`);
      }

      const data: EntityCountResponse = await response.json();

      if (data.success) {
        setTotalCount(data.count);
      }
    } catch (err: any) {
      console.error('❌ Error fetching entity count:', err);
      Sentry.captureException(err, {
        extra: { tableName, action: 'fetchEntityCount' },
      });
    }
  }, [tableName, session]);

  /**
   * Fetch searchable fields for the table
   */
  const fetchSearchableFields = useCallback(async () => {
    if (!session) return;

    try {
      const url = `${API_URL}/document-generation/entities/${tableName}/searchable-fields`;
      const response = await fetchWithAuth(url, { method: 'GET' }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch searchable fields: ${response.statusText}`);
      }

      const data: SearchableFieldsResponse = await response.json();

      if (data.success) {
        setSearchableDirectFields(data.directFields);
        setSearchableRelations(data.relationFields);
      }
    } catch (err: any) {
      console.error('❌ Error fetching searchable fields:', err);
      Sentry.captureException(err, {
        extra: { tableName, action: 'fetchSearchableFields' },
      });
    }
  }, [tableName, session]);

  /**
   * Search entities by related field value
   */
  const searchByRelatedField = useCallback(
    async (relationPath: string, fieldName: string, searchValue: string) => {
      if (!session || !searchValue.trim()) {
        return;
      }

      try {
        setIsSearching(true);
        setError(null);
        setActiveRelatedSearch({ relationPath, fieldName, searchValue });

        console.log(`🔍 Searching ${tableName} where ${relationPath}.${fieldName} contains "${searchValue}"`);

        const queryParams = new URLSearchParams({
          relationPath,
          fieldName,
          searchValue: searchValue.trim(),
          limit: '10',
        });

        const url = `${API_URL}/document-generation/entities/${tableName}/search?${queryParams.toString()}`;
        const response = await fetchWithAuth(url, { method: 'GET' }, session);

        if (!response.ok) {
          throw new Error(`Zoeken mislukt: ${response.statusText}`);
        }

        const data: EntitySearchResponse = await response.json();

        if (data.success) {
          console.log(`✅ Found ${data.totalMatches} matches, displaying ${data.count}`);
          setSearchResults(data.results);
          setTotalSearchMatches(data.totalMatches);
          setSearchResultIndex(0);

          if (data.results.length > 0) {
            setEntity(data.results[0]);
            toast.success(`${data.totalMatches} resultaten gevonden`, {
              position: 'bottom-right',
              autoClose: 3000,
            });
          } else {
            toast.warning('Geen resultaten gevonden', {
              position: 'bottom-right',
              autoClose: 3000,
            });
          }
        }
      } catch (err: any) {
        console.error('❌ Error searching entities:', err);
        setError(err.message || 'Zoeken mislukt');
        Sentry.captureException(err, {
          extra: { tableName, relationPath, fieldName, searchValue, action: 'searchByRelatedField' },
        });
        toast.error(`Fout bij zoeken: ${err.message}`, {
          position: 'top-right',
        });
      } finally {
        setIsSearching(false);
      }
    },
    [tableName, session]
  );

  /**
   * Navigate to next search result
   */
  const nextSearchResult = useCallback(() => {
    if (searchResults.length === 0) return;

    const newIndex = searchResultIndex >= searchResults.length - 1 ? 0 : searchResultIndex + 1;
    setSearchResultIndex(newIndex);
    setEntity(searchResults[newIndex]);
    toast.info(`Resultaat ${newIndex + 1} van ${searchResults.length}`, {
      position: 'bottom-right',
      autoClose: 1500,
    });
  }, [searchResults, searchResultIndex]);

  /**
   * Navigate to previous search result
   */
  const previousSearchResult = useCallback(() => {
    if (searchResults.length === 0) return;

    const newIndex = searchResultIndex <= 0 ? searchResults.length - 1 : searchResultIndex - 1;
    setSearchResultIndex(newIndex);
    setEntity(searchResults[newIndex]);
    toast.info(`Resultaat ${newIndex + 1} van ${searchResults.length}`, {
      position: 'bottom-right',
      autoClose: 1500,
    });
  }, [searchResults, searchResultIndex]);

  /**
   * Clear related field search and return to random mode
   */
  const clearRelatedSearch = useCallback(() => {
    setActiveRelatedSearch(null);
    setSearchResults([]);
    setSearchResultIndex(0);
    setTotalSearchMatches(0);
    fetchEntity(); // Fetch a random entity
    toast.info('Zoekopdracht gewist, willekeurige entiteit geladen', {
      position: 'bottom-right',
      autoClose: 2000,
    });
  }, [fetchEntity]);

  /**
   * Fetch a different random entity
   */
  const fetchDifferentEntity = useCallback(() => {
    console.log('🔄 Fetching different random entity...');
    // Clear any active search when fetching random
    setActiveRelatedSearch(null);
    setSearchResults([]);
    setSearchResultIndex(0);
    setTotalSearchMatches(0);
    fetchEntity(); // Fetch without ID to get random
    toast.info('Nieuwe willekeurige entiteit laden...', {
      position: 'bottom-right',
      autoClose: 2000,
    });
  }, [fetchEntity]);

  /**
   * Count properties that match search query
   */
  const countMatchingProperties = useCallback(() => {
    if (!entity || !searchQuery) return { matches: 0, total: 0 };

    let total = 0;
    let matches = 0;

    const countInEntity = (ent: EntityInstance) => {
      const props = Object.keys(ent.properties || {});
      total += props.length;

      const matchingProps = props.filter((key) =>
        key.toLowerCase().includes(searchQuery.toLowerCase())
      );
      matches += matchingProps.length;

      // Count in relations
      if (ent.relations) {
        ent.relations.forEach((rel) => {
          if (rel.data) {
            if (Array.isArray(rel.data)) {
              rel.data.forEach((nestedEntity) => countInEntity(nestedEntity));
            } else {
              countInEntity(rel.data);
            }
          }
        });
      }
    };

    countInEntity(entity);

    return { matches, total };
  }, [entity, searchQuery]);

  /**
   * Initial data fetch - only runs once per table, not on session refresh (tab switch)
   */
  useEffect(() => {
    if (!session || !tableName) return;

    // Check if tableName changed - if so, reset and fetch new data
    const tableNameChanged = currentTableNameRef.current !== tableName;
    if (tableNameChanged) {
      currentTableNameRef.current = tableName;
      isInitializedRef.current = false;
      // Reset all state for new table
      setEntity(null);
      setSearchResults([]);
      setSearchResultIndex(0);
      setTotalSearchMatches(0);
      setActiveRelatedSearch(null);
      setSearchableDirectFields([]);
      setSearchableRelations([]);
    }

    // Only fetch if not already initialized for this table
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      fetchEntity();
      fetchEntityCount();
      fetchSearchableFields();
    }
  }, [tableName, session, fetchEntity, fetchEntityCount, fetchSearchableFields]);

  const { matches: matchCount, total: totalPropertyCount } = countMatchingProperties();

  return {
    entity,
    loading,
    error,
    totalCount,
    searchQuery,
    setSearchQuery,
    fetchDifferentEntity,
    matchCount,
    totalPropertyCount,
    // Related field search
    searchableDirectFields,
    searchableRelations,
    searchByRelatedField,
    isSearching,
    activeRelatedSearch,
    searchResults,
    searchResultIndex,
    totalSearchMatches,
    nextSearchResult,
    previousSearchResult,
    clearRelatedSearch,
  };
};
